import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCoach } from "../store/coachStore";
import FormattedMessage from "./FormattedMessage";

const MODES = [
  { id: "chat" as const, label: "Chat", icon: "\ud83d\udcac" },
  { id: "essay_coach" as const, label: "Essay", icon: "\u270d\ufe0f" },
  { id: "counselor" as const, label: "Counselor", icon: "\ud83e\uddd1\u200d\ud83c\udfeb" },
  { id: "admissions" as const, label: "Analysis", icon: "\ud83c\udfaf" },
];

const WORKFLOWS = [
  { id: "weekly_checkin", label: "Weekly Check-in", icon: "\ud83d\udcc5" },
  { id: "college_list_builder", label: "Build College List", icon: "\ud83c\udfe0" },
  { id: "essay_brainstorm", label: "Essay Brainstorm", icon: "\u270d\ufe0f" },
  { id: "test_prep_plan", label: "Test Prep Plan", icon: "\ud83d\udcca" },
  { id: "activity_review", label: "Activity Review", icon: "\u26bd" },
  { id: "stress_check", label: "Stress Check-in", icon: "\ud83d\udc99" },
  { id: "decision_helper", label: "Decision Helper", icon: "\u2696\ufe0f" },
];

const QUICK_PROMPTS: Record<string, string[]> = {
  "/dashboard": ["What should I focus on this week?", "Am I on track?", "Review my progress"],
  "/colleges": ["Is my college list balanced?", "Help me research a school", "Should I add more safety schools?"],
  "/essays": ["Help me brainstorm essay topics", "What makes a great personal statement?"],
  "/deadlines": ["What deadlines are coming up?", "Explain ED vs EA vs RD"],
  "/scholarships": ["How do I find scholarships?", "Am I eligible for merit aid?"],
  "/roadmap": ["What's my next milestone?", "Am I behind schedule?"],
  "/activity-tracker": ["Are my activities strong enough?", "How can I deepen my involvement?"],
};

function getQuickPrompts(pathname: string): string[] {
  for (const [prefix, prompts] of Object.entries(QUICK_PROMPTS)) {
    if (pathname.startsWith(prefix)) return prompts;
  }
  return ["How can I improve my application?", "What should I work on next?", "Ask me anything about admissions"];
}

export default function CoachWidget() {
  const {
    isOpen, mode, messages, isStreaming, streamingText, error,
    close, toggle, setMode, sendMessage, newSession, clearError,
  } = useCoach();

  const [input, setInput] = useState("");
  const [showWorkflows, setShowWorkflows] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // ─── Drag-to-move state ────────────────────────────────
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!panelRef.current) return;
    isDragging.current = true;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isDragging.current) return;
      const x = Math.max(0, Math.min(window.innerWidth - 380, e.clientX - dragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.current.y));
      setPosition({ x, y });
    }
    function handleMouseUp() {
      isDragging.current = false;
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Reset position when closing
  useEffect(() => {
    if (!isOpen) setPosition(null);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  function handleSend() {
    if (!input.trim() || isStreaming) return;
    sendMessage(input);
    setInput("");
  }

  function handleQuickPrompt(prompt: string) {
    sendMessage(prompt);
  }

  function handleSelectWorkflow(workflow: string) {
    setMode("counselor", { workflow });
    setShowWorkflows(false);
    sendMessage(`I'd like to start a ${workflow.replace(/_/g, " ")} session.`);
  }

  const quickPrompts = getQuickPrompts(location.pathname);
  const showQuickPromptsBar = messages.length === 0 && !isStreaming && mode === "chat";

  // Panel positioning: use drag position if set, otherwise default bottom-right
  const panelStyle: React.CSSProperties = position
    ? { left: position.x, top: position.y, right: "auto", bottom: "auto" }
    : {};

  return (
    <>
      {/* Floating Bubble */}
      {!isOpen && (
        <button
          onClick={toggle}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full shadow-xl shadow-blue-500/30 flex items-center justify-center hover:scale-110 transition-all duration-200 group"
          aria-label="Open AI Coach"
        >
          <img src="/madrich-icon-v2.svg" alt="" className="w-8 h-8 rounded-lg" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          style={panelStyle}
          className={`fixed z-50 w-full sm:w-[380px] h-full sm:h-[520px] sm:rounded-2xl bg-white shadow-2xl shadow-slate-900/20 border border-slate-200 flex flex-col overflow-hidden ${
            position ? "" : "bottom-0 right-0 sm:bottom-4 sm:right-4"
          }`}
        >

          {/* Header — drag handle */}
          <div
            onMouseDown={handleMouseDown}
            className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 py-3 flex items-center justify-between shrink-0 cursor-move select-none"
          >
            <div className="flex items-center gap-2">
              <img src="/madrich-icon-v2.svg" alt="" className="w-6 h-6 rounded" />
              <span className="text-white font-bold text-sm">Madrich Coach</span>
              <span className="text-slate-500 text-[10px] hidden sm:inline">drag to move</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newSession} className="text-slate-400 hover:text-white p-1 transition-colors" title="New session">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              </button>
              <button onClick={close} className="text-slate-400 hover:text-white p-1 transition-colors" title="Close">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          {/* Mode Tabs */}
          <div className="flex border-b border-slate-100 px-2 shrink-0">
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => { setMode(m.id); setShowWorkflows(m.id === "counselor"); }}
                className={`flex-1 text-[11px] py-2.5 font-medium transition-all ${
                  mode === m.id ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <span className="text-sm">{m.icon}</span> {m.label}
              </button>
            ))}
          </div>

          {/* Workflow Picker (counselor mode) */}
          {showWorkflows && mode === "counselor" && (
            <div className="p-3 border-b border-slate-100 shrink-0 max-h-48 overflow-y-auto">
              <div className="grid grid-cols-2 gap-1.5">
                {WORKFLOWS.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => handleSelectWorkflow(w.id)}
                    className="text-left p-2 rounded-lg bg-slate-50 hover:bg-blue-50 hover:border-blue-200 border border-slate-100 transition-all"
                  >
                    <span className="text-sm">{w.icon}</span>
                    <div className="text-[10px] font-medium text-slate-700 mt-0.5">{w.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {/* Quick Prompts */}
            {showQuickPromptsBar && (
              <div className="space-y-1.5">
                <p className="text-xs text-slate-400 mb-2">Quick questions:</p>
                {quickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickPrompt(prompt)}
                    className="block w-full text-left text-xs bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-700 px-3 py-2 rounded-lg border border-slate-100 hover:border-blue-200 transition-all"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {/* Message bubbles */}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
                  msg.role === "user"
                    ? "bg-blue-600 text-white rounded-br-md"
                    : "bg-slate-50 text-slate-800 rounded-bl-md border border-slate-100"
                }`}>
                  {msg.role === "assistant" ? <FormattedMessage content={msg.content} /> : <p className="whitespace-pre-wrap">{msg.content}</p>}
                </div>
              </div>
            ))}

            {/* Streaming text */}
            {isStreaming && streamingText && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed bg-slate-50 text-slate-800 rounded-bl-md border border-slate-100">
                  <FormattedMessage content={streamingText} />
                  <span className="inline-block w-1.5 h-3 bg-blue-500 animate-pulse ml-0.5" />
                </div>
              </div>
            )}

            {/* Loading indicator (before first token) */}
            {isStreaming && !streamingText && (
              <div className="flex justify-start">
                <div className="bg-slate-50 rounded-2xl px-3 py-2 text-[13px] text-slate-400 animate-pulse border border-slate-100 rounded-bl-md">
                  Thinking...
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                {error}
                <button onClick={clearError} className="ml-2 underline">Dismiss</button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 px-3 py-2.5 flex gap-2 shrink-0 bg-white">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={mode === "counselor" ? "Continue the session..." : "Ask Madrich anything..."}
              disabled={isStreaming}
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={isStreaming || !input.trim()}
              className="bg-blue-600 text-white px-3 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
